"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef } from "react";

type ViewTransitionLike = {
  ready?: Promise<unknown>;
  finished?: Promise<unknown>;
  updateCallbackDone?: Promise<unknown>;
  skipTransition?: () => void;
};

type StartViewTransitionFn = (
  cb: () => void | Promise<void>,
) => ViewTransitionLike;

type ViewTransitionMode = "auto" | "on" | "off";

type MaybePatchedFn = StartViewTransitionFn & { __patchedByApp?: true };

const SESSION_KEY = "vt-mode";
const WATCHDOG_MS = 350;

const normalizeMode = (
  raw: string | null | undefined,
): ViewTransitionMode | null => {
  if (!raw) return null;
  const v = raw.trim().toLowerCase();
  if (v === "auto" || v === "on" || v === "off") return v;
  return null;
};

const isFirefox = () => /firefox/i.test(navigator.userAgent);

function getModeFromSession(): ViewTransitionMode | null {
  try {
    return normalizeMode(sessionStorage.getItem(SESSION_KEY));
  } catch {
    return null;
  }
}

function setModeToSession(mode: ViewTransitionMode) {
  try {
    sessionStorage.setItem(SESSION_KEY, mode);
  } catch {
    // ignore
  }
}

declare global {
  interface Window {
    __nativeStartViewTransition?: StartViewTransitionFn;
    __patchedStartViewTransition?: StartViewTransitionFn;
  }
}

const shouldSkipInAutoMode = (opts: { nextNavigationFlag: boolean }) => {
  if (isFirefox()) return true;
  if (opts.nextNavigationFlag) return true; // back/forward
  return false;
};

/**
 * next-view-transitions relies on `document.startViewTransition` existing.
 * It also attaches a popstate listener (back/forward) and wraps Link clicks.
 *
 * This controller patches `document.startViewTransition` and uses
 * `transition.skipTransition()` based on a configurable `?vt=` query param (persisted in sessionStorage):
 * - `vt=on`: never skip
 * - `vt=off`: always skip
 * - `vt=auto` (default): skip on Firefox, on popstate (back/forward), and when navigating to heavy routes
 *
 * On Chromium, this helps prevent the “page looks loaded but is not scrollable/interactive until timeout”
 * symptom when transitions get stuck or run too long.
 */
export function ViewTransitionsController() {
  const pathname = usePathname() ?? "";
  const searchParams = useSearchParams();

  useMemo(() => pathname, [pathname]);

  const didInitRef = useRef(false);
  const modeRef = useRef<ViewTransitionMode>("auto");
  const nextNavigationSkipRef = useRef(false);

  useEffect(() => {
    // Initialize mode once, then allow `?vt=` to override.
    if (didInitRef.current) return;
    const fromSession = getModeFromSession();
    modeRef.current = fromSession ?? "auto";
  }, []);

  useEffect(() => {
    // Read `?vt=` (persist) for quick testing.
    const vt = normalizeMode(searchParams?.get("vt"));
    if (!vt) return;
    modeRef.current = vt;
    setModeToSession(vt);
  }, [searchParams]);

  useEffect(() => {
    // Patch native startViewTransition once (if present).
    if (didInitRef.current) return;
    didInitRef.current = true;

    const current = (document as unknown as { startViewTransition?: unknown })
      .startViewTransition as StartViewTransitionFn | undefined;
    if (typeof current !== "function") return;

    // If already patched by us, do nothing.
    if ((current as MaybePatchedFn).__patchedByApp) {
      window.__patchedStartViewTransition = current;
      return;
    }

    window.__nativeStartViewTransition = current;
    const native = current;

    const patched: MaybePatchedFn = (callback) => {
      const mode = modeRef.current;

      const nextFlag = nextNavigationSkipRef.current;
      nextNavigationSkipRef.current = false;

      // Native `startViewTransition` must be called with `document` as `this`,
      // otherwise Chromium throws "Illegal invocation".
      const transition = native.call(document, callback);

      // `ready` rejects with an AbortError whenever a transition is skipped —
      // by us below or by the browser — and nothing else observes it, so each
      // skip surfaced as an unhandled rejection. A rejected `ready` only means
      // no animation ran; a failing update callback still rejects `finished`.
      transition?.ready?.catch(() => {});

      const shouldSkip =
        mode === "off" ||
        (mode === "auto" &&
          shouldSkipInAutoMode({
            nextNavigationFlag: nextFlag,
          }));

      if (shouldSkip) {
        transition?.skipTransition?.();
        return transition;
      }

      // Watchdog to prevent the “visible but frozen until timeout” experience.
      if (mode === "auto") {
        const done = transition?.finished;
        if (done && typeof done.finally === "function") {
          const t = window.setTimeout(() => {
            transition?.skipTransition?.();
          }, WATCHDOG_MS);
          void done.finally(() => window.clearTimeout(t));
        }
      }

      return transition;
    };
    patched.__patchedByApp = true;

    const docAny = document as unknown as {
      startViewTransition?: StartViewTransitionFn;
    };
    docAny.startViewTransition = patched;
    window.__patchedStartViewTransition = patched;
  }, []);

  useEffect(() => {
    // Back/forward detection: we set a flag that the patched startViewTransition reads synchronously.
    const native = window.__nativeStartViewTransition;
    if (!native) return;

    const onPopStateCapture = () => {
      if (modeRef.current === "on") return;
      nextNavigationSkipRef.current = true;
    };

    window.addEventListener("popstate", onPopStateCapture, { capture: true });

    return () => {
      window.removeEventListener("popstate", onPopStateCapture, {
        capture: true,
      } as AddEventListenerOptions);
    };
  }, []);

  return null;
}
